import * as React from "react";
import { userNavigationPreferenceIntegration } from "@/lib/api/client.js";
import {
  resolveSidebarWorkspacePreferences,
  useSidebarPreferencesStore,
} from "@/store/sidebar-preferences.store.js";

const SAVE_DELAY_MS = 400;

export function useUserNavigationPreferenceSync(scopeKey) {
  const storedPreferences = useSidebarPreferencesStore((state) => (
    scopeKey ? state.workspacePreferencesByScope[scopeKey] : undefined
  ));
  const syncPending = useSidebarPreferencesStore((state) => Boolean(
    scopeKey && state.workspacePreferenceSyncPendingByScope[scopeKey],
  ));
  const revision = useSidebarPreferencesStore((state) => (
    scopeKey ? state.workspacePreferenceRevisionsByScope[scopeKey] || 0 : 0
  ));
  const preferences = React.useMemo(
    () => resolveSidebarWorkspacePreferences(storedPreferences),
    [storedPreferences],
  );
  const [bootstrappedScope, setBootstrappedScope] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setBootstrappedScope(null);
    if (!scopeKey) return () => { cancelled = true; };

    const persistCurrentPreferences = async () => {
      const state = useSidebarPreferencesStore.getState();
      const currentRevision = state.workspacePreferenceRevisionsByScope[scopeKey] || 0;
      const currentPreferences = resolveSidebarWorkspacePreferences(
        state.workspacePreferencesByScope[scopeKey],
      );
      const result = await userNavigationPreferenceIntegration.savePreferences(
        scopeKey,
        currentPreferences,
      );
      if (!cancelled && result.success) {
        useSidebarPreferencesStore.getState().markWorkspacePreferencesSynced(
          scopeKey,
          currentRevision,
        );
      }
    };

    const bootstrap = async () => {
      const startingState = useSidebarPreferencesStore.getState();
      const startingRevision = startingState.workspacePreferenceRevisionsByScope[scopeKey] || 0;
      const wasPending = Boolean(
        startingState.workspacePreferenceSyncPendingByScope[scopeKey],
      );

      if (wasPending) {
        await persistCurrentPreferences();
      } else {
        const result = await userNavigationPreferenceIntegration.fetchPreferences();
        if (cancelled) return;

        const currentState = useSidebarPreferencesStore.getState();
        const currentRevision = currentState.workspacePreferenceRevisionsByScope[scopeKey] || 0;
        const changedWhileLoading = currentRevision !== startingRevision
          || currentState.workspacePreferenceSyncPendingByScope[scopeKey];

        if (changedWhileLoading) {
          await persistCurrentPreferences();
        } else if (result.success && result.data) {
          currentState.setWorkspacePreferencesFromServer(scopeKey, result.data);
        } else if (result.success) {
          currentState.markWorkspacePreferenceSyncPending(scopeKey);
          await persistCurrentPreferences();
        }
      }

      if (!cancelled) setBootstrappedScope(scopeKey);
    };

    void bootstrap();
    return () => { cancelled = true; };
  }, [scopeKey]);

  React.useEffect(() => {
    if (!scopeKey || bootstrappedScope !== scopeKey || !syncPending) return undefined;

    let cancelled = false;
    const timeout = setTimeout(async () => {
      const result = await userNavigationPreferenceIntegration.savePreferences(
        scopeKey,
        preferences,
      );
      if (!cancelled && result.success) {
        useSidebarPreferencesStore.getState().markWorkspacePreferencesSynced(
          scopeKey,
          revision,
        );
      }
    }, SAVE_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [bootstrappedScope, preferences, revision, scopeKey, syncPending]);
  return bootstrappedScope === scopeKey;
}
